import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, type Rol } from '@/hooks/useAuth';

interface NavItem {
  label: string;
  icon: string;
  path: string;
  roles: Rol[];
  badge?: number;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', icon: 'ri-dashboard-line', path: '/dashboard', roles: ['admin', 'cotizador', 'contabilidad'] },
  { label: 'Clientes', icon: 'ri-user-star-line', path: '/dashboard/clientes', roles: ['admin', 'cotizador', 'contabilidad'] },
  { label: 'Catalogo Conceptos', icon: 'ri-booklet-line', path: '/dashboard/catalogo', roles: ['admin', 'cotizador'] },
  { label: 'Proveedores', icon: 'ri-store-2-line', path: '/dashboard/proveedores', roles: ['admin', 'contabilidad'] },
  { label: 'Contabilidad', icon: 'ri-bank-line', path: '/dashboard/contabilidad', roles: ['admin', 'contabilidad'] },
  { label: 'Nominas', icon: 'ri-user-received-line', path: '/dashboard/nominas', roles: ['admin', 'contabilidad'] },
  { label: 'Trabajadores', icon: 'ri-team-line', path: '/dashboard/trabajadores', roles: ['admin', 'contabilidad'] },
  { label: 'Configuración', icon: 'ri-settings-line', path: '/dashboard/configuracion', roles: ['admin'] },
  { label: 'Usuarios', icon: 'ri-user-settings-line', path: '/dashboard/usuarios', roles: ['admin'] },
];

export default function Sidebar({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { usuario, logout, cargando } = useAuth();

  // Mientras se verifica la sesión, mostramos una versión mínima sin datos sensibles
  if (cargando || !usuario) {
    return (
      <aside
        className={`fixed left-0 top-0 h-full bg-background-50 border-r border-background-200 flex flex-col z-40 transition-all duration-300 ${
          collapsed ? 'w-[68px]' : 'w-[240px]'
        }`}
      >
        <div className="flex items-center gap-3 px-4 h-16 border-b border-background-200 flex-shrink-0">
          <div className="w-9 h-9 rounded-xl bg-primary-500 flex items-center justify-center flex-shrink-0">
            <img
              src="https://storage.readdy-site.link/project_files/66d984af-80c8-4c38-95eb-a0b94df9eefb/09013345-09ca-40f7-81cb-457bb9b8322e_compressed_imagen_2026-07-26_133258629.webp"
              alt="AIRMEX"
              className="w-full h-full object-contain p-0.5"
            />
          </div>
          {!collapsed && (
            <span className="font-heading font-semibold text-foreground-900 text-sm whitespace-nowrap">
              AIRMEX
            </span>
          )}
        </div>
        {/* Contenido esqueleto o vacío */}
        <div className="flex-1 flex items-center justify-center">
          <i className="ri-loader-4-line animate-spin text-foreground-300 text-xl" />
        </div>
      </aside>
    );
  }

  const filteredItems = navItems.filter((item) =>
    item.roles.includes(usuario.rol)
  );

  const isActive = (path: string) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <aside
      className={`fixed left-0 top-0 h-full bg-background-50 border-r border-background-200 flex flex-col z-40 transition-all duration-300 ${
        collapsed ? 'w-[68px]' : 'w-[240px]'
      }`}
    >
      <div className="flex items-center gap-3 px-4 h-16 border-b border-background-200 flex-shrink-0">
        <div className="w-9 h-9 rounded-xl bg-primary-500 flex items-center justify-center flex-shrink-0 overflow-hidden">
          <img
            src="https://storage.readdy-site.link/project_files/66d984af-80c8-4c38-95eb-a0b94df9eefb/09013345-09ca-40f7-81cb-457bb9b8322e_compressed_imagen_2026-07-26_133258629.webp"
            alt="AIRMEX"
            className="w-full h-full object-contain p-0.5"
          />
        </div>
        {!collapsed && (
          <span className="font-heading font-semibold text-foreground-900 text-sm whitespace-nowrap">
            AIRMEX
          </span>
        )}
        <button
          onClick={onToggle}
          className="ml-auto w-7 h-7 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer flex-shrink-0"
        >
          <i className={`text-sm ${collapsed ? 'ri-menu-unfold-line' : 'ri-menu-fold-line'}`} />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
        {filteredItems.map((item) => (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 cursor-pointer whitespace-nowrap group ${
              isActive(item.path)
                ? 'bg-primary-100/70 text-primary-700 font-medium'
                : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-800'
            }`}
            title={collapsed ? item.label : undefined}
          >
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className={`${item.icon} text-base`} />
            </div>
            {!collapsed && (
              <>
                <span className="flex-1 text-left">{item.label}</span>
                {item.badge && (
                  <span className="flex-shrink-0 min-w-[20px] h-5 flex items-center justify-center rounded-full bg-accent-500 text-background-50 text-[10px] font-semibold px-1.5">
                    {item.badge}
                  </span>
                )}
              </>
            )}
            {collapsed && item.badge && (
              <span className="absolute -top-1 -right-1 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-accent-500 text-background-50 text-[9px] font-semibold px-1">
                {item.badge}
              </span>
            )}
          </button>
        ))}
      </nav>

<div className="border-t border-background-200 p-3">
  <div className={`flex items-center gap-3 ${collapsed ? 'justify-center' : 'px-1'}`}>
    <div className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center flex-shrink-0">
      <span className="text-xs font-semibold text-secondary-700">
        {usuario?.nombre?.charAt(0) ?? usuario?.email?.charAt(0) ?? '?'}
      </span>
    </div>
    {!collapsed && (
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-foreground-800 truncate">
          {usuario?.nombre ?? usuario?.email ?? 'Usuario'}
        </p>
        <p className="text-[10px] text-foreground-400 capitalize truncate">
          {usuario?.rol ?? 'sin rol'}
        </p>
      </div>
    )}
    <button
      onClick={handleLogout}
      className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-foreground-400 hover:text-red-500 transition-colors cursor-pointer flex-shrink-0"
      title="Cerrar sesión"
    >
      <i className="ri-logout-box-r-line text-sm" />
    </button>
  </div>
</div>
    </aside>
  );
}